// BOORO: a shared dashboard. It shows the numbers of a period and nothing
// else, so a link can be passed on without giving away the tasks behind it.
import { formatCost, formatSavedMinutes, formatSuccessRate } from "@booro/tasks";
import { Trans, useLingui } from "@lingui/react/macro";
import type { DashboardShare } from "@rakazo/contracts";
import { BotAvatar } from "@rakazo/ui-web";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ModelBreakdown } from "../components/ModelBreakdown";
import { rpc } from "../lib/rpc";
import { errorText } from "../lib/user-error";

export function SharedDashboardPage() {
  const { t } = useLingui();
  const { shareId } = useParams();
  const [share, setShare] = useState<DashboardShare | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!shareId) return;
    let active = true;
    void rpc.booroDashboard
      .readShare({ shareId })
      .then((next) => {
        if (active) setShare(next);
      })
      .catch((cause) => {
        if (active) setError(errorText(cause, t`This link is no longer available`));
      });
    return () => {
      active = false;
    };
  }, [shareId, t]);

  if (error) {
    return (
      <main className="flex min-h-full items-center justify-center bg-background px-6">
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      </main>
    );
  }
  if (!share) {
    return <main className="min-h-full bg-background" aria-hidden="true" />;
  }

  return (
    <main className="min-h-full bg-background px-6 py-10">
      <div className="mx-auto max-w-3xl">
        <h1 className="text-[24px] font-semibold text-foreground">
          <Trans>BOORO report</Trans>
        </h1>
        <p className="mt-1 text-[13.5px] text-muted-foreground">
          {new Date(share.from).toLocaleDateString()} – {new Date(share.to).toLocaleDateString()}
        </p>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-2xl border border-border bg-card px-4 py-3">
            <p className="text-[12.5px] text-muted-foreground">
              <Trans>Tasks done</Trans>
            </p>
            <p className="mt-1 text-[22px] font-semibold text-foreground">{share.stats.done}</p>
          </div>
          <div className="rounded-2xl border border-border bg-card px-4 py-3">
            <p className="text-[12.5px] text-muted-foreground">
              <Trans>Success rate</Trans>
            </p>
            <p className="mt-1 text-[22px] font-semibold text-foreground">
              {formatSuccessRate(share.stats.successRate)}
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card px-4 py-3">
            <p className="text-[12.5px] text-muted-foreground">
              <Trans>Time saved</Trans>
            </p>
            <p className="mt-1 text-[22px] font-semibold text-foreground">
              {formatSavedMinutes(share.stats.minutesSaved)}
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card px-4 py-3">
            <p className="text-[12.5px] text-muted-foreground">
              <Trans>Cost</Trans>
            </p>
            <p className="mt-1 text-[22px] font-semibold text-foreground">
              {formatCost(share.stats.costUsd)}
            </p>
          </div>
        </div>

        {share.agents.length > 0 ? (
          <div className="mt-6 rounded-2xl border border-border bg-card">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-border text-[12px] tracking-wider uppercase text-muted-foreground">
                  <th className="px-4 py-2.5 text-start font-medium">
                    <Trans>Agent</Trans>
                  </th>
                  <th className="px-4 py-2.5 text-end font-medium">
                    <Trans>Done</Trans>
                  </th>
                  <th className="px-4 py-2.5 text-end font-medium">
                    <Trans>Success rate</Trans>
                  </th>
                  <th className="px-4 py-2.5 text-end font-medium">
                    <Trans>Time saved</Trans>
                  </th>
                  <th className="px-4 py-2.5 text-end font-medium">
                    <Trans>Cost</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {share.agents.map((agent) => (
                  <tr key={agent.botId} className="border-b border-border last:border-b-0">
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-2">
                        <BotAvatar color={agent.botColor} identity={agent.botId} size={20} />
                        <span className="truncate" dir="auto">
                          {agent.botName}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-end">{agent.done}</td>
                    <td className="px-4 py-2.5 text-end">{formatSuccessRate(agent.successRate)}</td>
                    <td className="px-4 py-2.5 text-end">
                      {formatSavedMinutes(agent.minutesSaved)}
                    </td>
                    <td className="px-4 py-2.5 text-end">{formatCost(agent.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <ModelBreakdown models={share.models} />
      </div>
    </main>
  );
}
